import { type FC } from "react";

import { Home, Building2, Scale, Landmark, Users } from "lucide-react";

import { OptionCard } from "@/shared/components/ui/OptionCard";
import { useAppContext } from "@/shared/hooks/useAppContext";

import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";

const ATTESTED_KEY = "admin_will_search_attested";
const ATTESTED_AT_KEY = "admin_will_search_attested_at";

interface PlaceRow {
  icon: FC<{ className?: string }>;
  text: (name: string) => string;
}

const PLACES: PlaceRow[] = [
  {
    icon: Home,
    text: (name) => `${name}'s papers at home, like a desk, file cabinet, or home safe`,
  },
  {
    icon: Building2,
    text: (name) => `A safe deposit box at ${name}'s bank`,
  },
  {
    icon: Scale,
    text: (name) => `An attorney ${name} worked with, who may keep the original`,
  },
  {
    icon: Landmark,
    text: () => "Surrogate's Court, where some people file their will for safekeeping",
  },
  {
    icon: Users,
    text: () => "Family members or close friends who might have a copy",
  },
];

export const WillSearchScreen: FC<FlowCustomComponentProps> = ({
  answers,
  setAnswer,
}) => {
  const { person } = useAppContext();
  const name = person?.first_name ?? "the decedent";

  const isAttested = answers[ATTESTED_KEY] === "true";

  const handleToggle = () => {
    if (isAttested) {
      setAnswer(ATTESTED_KEY, "");
      setAnswer(ATTESTED_AT_KEY, "");
    } else {
      setAnswer(ATTESTED_KEY, "true");
      setAnswer(ATTESTED_AT_KEY, new Date().toISOString());
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <p className="font-sans text-xl font-semibold leading-[30px]">
          Make sure there's no will
        </p>
        <p className="text-sm text-secondary tracking-[-0.28px]">
          Before the court appoints an administrator, you confirm that you looked for a will
          and didn't find one. If one turns up later, the process changes, so a careful check
          now saves you from starting over.
        </p>
      </div>

      <div className="border-t border-primary" />

      <div className="flex flex-col gap-4">
        <p className="text-sm font-semibold">Common places to find a will</p>

        <div className="flex flex-col divide-y divide-primary rounded-lg border border-primary">
          {PLACES.map((place, i) => {
            const Icon = place.icon;
            return (
              <div key={i} className="flex items-center gap-3 px-4 py-3">
                <Icon className="h-5 w-5 shrink-0 text-brand" />
                <span className="text-sm">{place.text(name)}</span>
              </div>
            );
          })}
        </div>
      </div>

      <OptionCard
        label="I've looked in each of these places"
        selected={isAttested}
        onClick={handleToggle}
        variant="checkbox"
      />
    </div>
  );
};
